// mindmetal-app/web/src/pages/employee/mindmetal/PersonaTesterTab.tsx
import { useState } from 'react'
import { motion } from 'framer-motion'
import { User, Wand2, ArrowRight, RefreshCw, Copy, Check, ChevronDown, ChevronRight, Download, FileCode } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, Button, Input, Select, Badge } from '@personic/shared'
import { useTestUsers } from '@/hooks/useMindMetaLTestUsers'
import PersonaViewer from '@/components/personas/PersonaViewer'
import type { Persona } from '@/hooks/usePersonas'
import { api } from '@personic/shared'

const GENERATION_MODES = [
  { value: 'full', label: 'Full', description: 'All personality dimensions and context' },
  { value: 'partial', label: 'Partial', description: 'Key dimensions only' },
  { value: 'minimal', label: 'Minimal', description: 'Communication style only' },
]

const SENSITIVITY_MODES = [
  { value: 'standard', label: 'Standard', description: 'Full insights for internal use' },
  { value: 'external', label: 'External', description: 'Filtered for customer-facing' },
  { value: 'anonymized', label: 'Anonymized', description: 'Aggregate patterns only' },
]

export function PersonaTesterTab() {
  const [profileId, setProfileId] = useState('')
  const [selectedCanonicalId, setSelectedCanonicalId] = useState('')
  const [generationMode, setGenerationMode] = useState<'full' | 'partial' | 'minimal'>('full')
  const [sensitivityMode, setSensitivityMode] = useState<'standard' | 'external' | 'anonymized'>(
    'standard'
  )
  const [persona, setPersona] = useState<Persona | null>(null)
  const [previousPersona, setPreviousPersona] = useState<Persona | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [showComparison, setShowComparison] = useState(false)
  const [exportFormat, setExportFormat] = useState<'narrative' | 'yaml' | 'json'>('narrative')
  const [exportContent, setExportContent] = useState<string | null>(null)
  const [exportLoading, setExportLoading] = useState(false)
  const [exportCopied, setExportCopied] = useState(false)

  const { data: testUsers } = useTestUsers(1, 50)

  const testUserOptions = [
    { value: '', label: 'Select a test user...' },
    ...(testUsers?.test_users.map((u) => ({
      value: u.canonical_id,
      label: `${u.display_name} (${u.canonical_id})`,
    })) || []),
  ]

  const handleGenerate = async () => {
    const targetId = selectedCanonicalId || profileId
    if (!targetId) return

    setError(null)
    setIsLoading(true)

    // Save previous persona for comparison
    if (persona) {
      setPreviousPersona(persona)
    }

    try {
      const response = await api.post<Persona>(
        `/api/mindmetal/v3/admin/test-users/${targetId}/persona`,
        {
          mode: generationMode,
          sensitivity_mode: sensitivityMode,
          product_context: 'engage',
        }
      )
      setPersona(response.data)
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.message || 'Failed to generate persona'
      setError(detail)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCopy = async () => {
    if (!persona) return
    await navigator.clipboard.writeText(JSON.stringify(persona, null, 2))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleRegenerate = () => {
    if (persona) {
      setPreviousPersona(persona)
      setPersona(null)
    }
    handleGenerate()
  }

  const handleExport = async (fmt: 'narrative' | 'yaml' | 'json') => {
    const targetId = selectedCanonicalId || profileId
    if (!targetId) return
    setExportFormat(fmt)
    setExportLoading(true)
    try {
      const response = await api.get(
        `/api/mindmetal/v3/admin/test-users/${targetId}/persona/export?format=${fmt}`,
        { responseType: 'text' }
      )
      setExportContent(typeof response.data === 'string' ? response.data : JSON.stringify(response.data, null, 2))
    } catch (err: any) {
      setExportContent(`Error: ${err.response?.data?.detail || err.message}`)
    } finally {
      setExportLoading(false)
    }
  }

  const handleExportCopy = async () => {
    if (!exportContent) return
    await navigator.clipboard.writeText(exportContent)
    setExportCopied(true)
    setTimeout(() => setExportCopied(false), 2000)
  }

  const handleExportDownload = () => {
    if (!exportContent) return
    const targetId = selectedCanonicalId || profileId
    const ext = exportFormat === 'json' ? 'json' : exportFormat === 'yaml' ? 'yaml' : 'txt'
    const blob = new Blob([exportContent], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${targetId}_persona.${ext}`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">
      {/* Input Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-copper-600" />
            Persona Generation Tester
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Profile Selection */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Select
                  label="Select Test User"
                  options={testUserOptions}
                  value={selectedCanonicalId}
                  onChange={(e) => {
                    setSelectedCanonicalId(e.target.value)
                    setProfileId('')
                  }}
                />
              </div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Input
                    label="Or Enter Profile ID"
                    value={profileId}
                    onChange={(e) => {
                      setProfileId(e.target.value)
                      setSelectedCanonicalId('')
                    }}
                    placeholder="canonical_id or profile_id"
                  />
                </div>
              </div>
            </div>

            {/* Generation Mode */}
            <div>
              <label className="block text-sm font-medium text-midnight-700 mb-2">
                Generation Mode
              </label>
              <div className="grid grid-cols-3 gap-3">
                {GENERATION_MODES.map((mode) => (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setGenerationMode(mode.value as 'full' | 'partial' | 'minimal')}
                    className={`p-4 rounded-xl border text-left transition-all ${
                      generationMode === mode.value
                        ? 'border-copper-500 bg-copper-50'
                        : 'border-warm-200 hover:border-warm-300'
                    }`}
                  >
                    <div className="font-medium text-midnight-800">{mode.label}</div>
                    <div className="text-xs text-warm-500 mt-1">{mode.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Sensitivity Mode */}
            <div>
              <label className="block text-sm font-medium text-midnight-700 mb-2">
                Sensitivity Mode
              </label>
              <div className="grid grid-cols-3 gap-3">
                {SENSITIVITY_MODES.map((mode) => (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() =>
                      setSensitivityMode(mode.value as 'standard' | 'external' | 'anonymized')
                    }
                    className={`p-4 rounded-xl border text-left transition-all ${
                      sensitivityMode === mode.value
                        ? 'border-sage-500 bg-sage-50'
                        : 'border-warm-200 hover:border-warm-300'
                    }`}
                  >
                    <div className="font-medium text-midnight-800">{mode.label}</div>
                    <div className="text-xs text-warm-500 mt-1">{mode.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                onClick={handleGenerate}
                isLoading={isLoading}
                disabled={!profileId && !selectedCanonicalId}
                leftIcon={<Wand2 className="w-4 h-4" />}
              >
                Generate Persona
              </Button>
              {persona && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleRegenerate}
                    disabled={isLoading}
                    leftIcon={<RefreshCw className="w-4 h-4" />}
                  >
                    Regenerate
                  </Button>
                  <Button variant="ghost" onClick={handleCopy}>
                    {copied ? (
                      <Check className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
          {error}
        </div>
      )}

      {/* Comparison Toggle */}
      {persona && previousPersona && (
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowComparison(!showComparison)}
            className="flex items-center gap-2 text-sm text-copper-600 hover:text-copper-700"
          >
            {showComparison ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            {showComparison ? 'Hide Comparison' : 'Show Comparison with Previous'}
          </button>
          <Badge variant="sage" size="sm">
            Before/After Available
          </Badge>
        </div>
      )}

      {/* Results */}
      {showComparison && previousPersona && persona ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid lg:grid-cols-2 gap-6"
        >
          <div>
            <h3 className="text-sm font-medium text-warm-500 mb-3">Previous Persona</h3>
            <PersonaViewer persona={previousPersona} />
          </div>
          <div>
            <h3 className="text-sm font-medium text-copper-600 mb-3 flex items-center gap-2">
              <ArrowRight className="w-4 h-4" />
              New Persona
            </h3>
            <PersonaViewer persona={persona} />
          </div>
        </motion.div>
      ) : persona ? (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <PersonaViewer persona={persona} />
        </motion.div>
      ) : null}

      {/* Export as Injectable Prompt */}
      {persona && (selectedCanonicalId || profileId) && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileCode className="w-5 h-5 text-copper-600" />
                Export as Injectable Prompt
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 mb-4">
                {(['narrative', 'yaml', 'json'] as const).map((fmt) => (
                  <Button
                    key={fmt}
                    variant={exportFormat === fmt && exportContent ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => handleExport(fmt)}
                    isLoading={exportLoading && exportFormat === fmt}
                  >
                    {fmt.toUpperCase()}
                  </Button>
                ))}
                {exportContent && (
                  <>
                    <div className="flex-1" />
                    <Button variant="ghost" size="sm" onClick={handleExportCopy}>
                      {exportCopied ? (
                        <Check className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleExportDownload}>
                      <Download className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
              {exportContent && (
                <pre className="p-4 bg-midnight-900 text-warm-100 rounded-xl overflow-x-auto text-sm font-mono whitespace-pre-wrap max-h-96">
                  {exportContent}
                </pre>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  )
}
